import { selectors as appSelectors } from '@/api/app';
import { useSelector } from '@/api/state';
import { actions as usersActions } from '@/api/users';
import { BUILD_VERSION, isLocalBuild } from '@/utils/buildVersion';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { IntercomProps, useIntercom } from 'react-use-intercom';

declare global {
  interface Window {
    profitwell: any;
  }
}

const setupIntercom = (
  update: (props?: Partial<IntercomProps>) => void,
  myProfile: {
    publicId: string;
    intercomHash: string;
    email: string;
    name: string;
    contactNumber: string;
    accounts: { id: number; companyName: string }[];
  }
) => {
  // TODO: only send to intercom if specifc details changed
  if (myProfile.name && myProfile.name !== 'undefined undefined') {
    update({
      // IMPORTANT: userId should be unique and never change for a user
      userId: myProfile.publicId,
      userHash: myProfile.intercomHash,
      email: myProfile.email,
      name: myProfile.name,
      phone: myProfile.contactNumber,
      company: {
        companyId:
          myProfile.accounts &&
          myProfile.accounts[0] &&
          myProfile.accounts[0].id.toString(),
        name:
          myProfile.accounts &&
          myProfile.accounts[0] &&
          myProfile.accounts[0].companyName,
      },
      ...(isLocalBuild
        ? {}
        : { customAttributes: { app_version: BUILD_VERSION } }),
    });
  }
};

const useDevelopmentTools = (): void => {
  const dispatch = useDispatch();
  const myProfile = useSelector((state: any) => state.users.myProfile);
  const isLoggedIn = useSelector((state: any) =>
    appSelectors.isLoggedIn(state.app)
  );

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(usersActions.getProfile());
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [isLoggedIn]);

  const setupSentry = useCallback(() => {
    const scope = Sentry.getCurrentScope();
    scope.setUser({
      email: myProfile.email,
    });
  }, [myProfile]);

  const setupProfitWell = useCallback(() => {
    const stripeCustomerId = myProfile?.accounts?.[0]?.stripeCustomerId;
    window.profitwell('start', {
      user_email: myProfile.email,
      user_id: stripeCustomerId,
    });
  }, [myProfile]);

  const handleSetupProfitwell = useCallback(() => {
    if (myProfile && window.profitwell) {
      if (
        import.meta.env.VITE_APP_ENV !== 'local' &&
        import.meta.env.VITE_APP_ENV !== 'development' &&
        import.meta.env.VITE_ENV !== 'staging'
      ) {
        // only send to profitwell on prod
        setupProfitWell();
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has an unnecessary dependency: window.
  }, [window.profitwell, setupProfitWell, myProfile]);

  const { update } = useIntercom();
  useEffect(() => {
    //  setup data when profile has loaded
    if (myProfile && Object.keys(myProfile).length > 0) {
      setupIntercom(update, myProfile);
      handleSetupProfitwell();
      if (import.meta.env.VITE_APP_ENV !== 'local') {
        setupSentry();
      }
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'update', 'handleSetupProfitwell', and 'setupSentry'
  }, [myProfile]);
};

export default useDevelopmentTools;
